// Save / print for PDFs the app renders itself (vouchers) inside the Android
// app. Its WebView can't do either: window.print() is a no-op there and a
// blob: download is silently dropped, so the finished PDF goes to the native
// PdfBridge plugin instead. Browsers keep their normal print/download path.

export const isNativeApp = !!(window as any).Capacitor?.isNativePlatform?.();

/** False on app builds from before PdfBridge — those need an app update. */
export const hasNativePdf = (): boolean =>
  isNativeApp && !!(window as any).Capacitor?.isPluginAvailable?.('PdfBridge');

export const NATIVE_PDF_UPDATE_MSG =
  'Update the ABS Cloud app from the latest APK to print or download on this phone.';

// Boxed for the same reason as callerId.ts: registerPlugin returns a Proxy
// that turns any property access into a native call, so letting it sit on an
// await path makes the runtime probe `.then` and blow up.
interface PluginBox { p: any }
let pluginPromise: Promise<PluginBox> | null = null;

function getPluginBox(): Promise<PluginBox> {
  if (!pluginPromise) {
    pluginPromise = import('@capacitor/core').then(({ registerPlugin }) => ({ p: registerPlugin('PdfBridge') }));
  }
  return pluginPromise;
}

const blobToBase64 = (blob: Blob) => new Promise<string>((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result).replace(/^data:[^,]*,/, ''));
  reader.onerror = () => reject(reader.error || new Error('Could not read the PDF'));
  reader.readAsDataURL(blob);
});

/** Saves to the phone's Downloads and opens it in the installed PDF viewer. */
export async function savePdf(blob: Blob, filename: string): Promise<void> {
  if (!hasNativePdf()) throw new Error(NATIVE_PDF_UPDATE_MSG);
  const box = await getPluginBox();
  await box.p.save({ data: await blobToBase64(blob), filename });
}

/** Hands the PDF to the Android share sheet (WhatsApp, Gmail, Drive…) with
 *  an optional message as the shared text. */
export async function sharePdf(blob: Blob, filename: string, text?: string): Promise<void> {
  if (!hasNativePdf()) throw new Error(NATIVE_PDF_UPDATE_MSG);
  const box = await getPluginBox();
  await box.p.share({ data: await blobToBase64(blob), filename, text: text || '' });
}

/** Opens the Android print dialog for the PDF. */
export async function printPdf(blob: Blob, filename: string): Promise<void> {
  if (!hasNativePdf()) throw new Error(NATIVE_PDF_UPDATE_MSG);
  const box = await getPluginBox();
  await box.p.print({ data: await blobToBase64(blob), filename });
}
